import { getPrivateFirestoreUrl } from './encoding-helpers';
import { q1AgreementFunctions } from './q1-core-functions';
import {
  generateAndStoreKeyPair, getKeyPair, generateSignedHash, exportPublicKeyToSPKI,
  importPublicSpkiKey, verifySignedHash, generateHash,
} from './crypto-helpers';
import { capFirstChar } from './string-helpers-client.js';

const CONTRIBUTOR_UPGRADE_AGREEMENT_TYPE = 'q1-membership-contributor';
const CONTRIBUTOR_UPGRADE_IN_PROGRESS_FLAG = 'contributor-upgrade-in-progress';

/** First item is the font family. Second is the google fonts key. */
export const signatureFonts = [
  ['Cedarville Cursive', 'Cedarville+Cursive'],
  ['Calligraffitti', 'Calligraffitti'],
  ['Over the Rainbow', 'Over+the+Rainbow'],
  ['Permanent Marker', 'Permanent+Marker'],
  ['Indie Flower', 'Indie+Flower'],
  ['La Belle Aurore', 'La+Belle+Aurore'],
  ['Meddon', 'Meddon'],
  ['Mr De Haviland', 'Mr+De+Haviland'],
  ['Alex Brush', 'Alex+Brush'],
  ['Sacramento', 'Sacramento'],
  ['Great Vibes', 'Great+Vibes'],
  ['Monsieur La Doulaise', 'Monsieur+La+Doulaise'],
];

/**
 * Contains the next steps for each agreement type, if any. These are displayed to the member
 * after the agreement is approved.
 */
export const nextStepsByType = {
  [CONTRIBUTOR_UPGRADE_AGREEMENT_TYPE]: {
    message: 'Agreement signed! Let\'s finish off the contributor upgrade process...',
    ctaPath: '/m/contributor-upgrade',
    ctaText: 'Continue Upgrade',
    activeFor: 'subject',
    activeWhen: { flag: CONTRIBUTOR_UPGRADE_IN_PROGRESS_FLAG },
  },
};


export class AgreementManager {

  constructor(currentMember) {
    this._resetInitParams();

    this.currentMember = currentMember;
    this.keyPair = null;

    // Get the existing key pair, create one if missing
    this._initKeyPair();
  }

  _resetInitParams() {
    this.ready = false;
    this.initComplete = new Promise((resolve, reject) => {
      this._resolveInit = resolve;
      this._rejectInit = reject;
    });
  }

  async _initKeyPair() {
    try {
      // Get the existing key pair from indexed db if present
      const keyPair = await this.getKeyPair();
      
      // Else create a new one
      if (!keyPair) await this.generateNewKeyPair();
      
      // Resolve the promise (notifying parent element that agreements may be signed now)
      this.ready = true;
      this._resolveInit();
    } catch (error) {
      // Pass any issues to parent element so it can present to member
      this._rejectInit(error);
    }
  }

  /**
   * Kicks off the contributor upgrade process. The backend will throw an error if the member
   * is not a network level member.
   * 
   * @returns {String} equal to agreementId
   */
  async startContributorUpgrade() {
    const { getOrCreateContributorUpgrade } = q1AgreementFunctions;
    
    try {
      const response = await getOrCreateContributorUpgrade();
      const { agreementId } = response.data || {};
      
      return agreementId;
    } catch (error) {
      console.error('Error starting upgrade:', error);
      throw error;
    }
  }

  /**
   * Queries for an agreement.
   * 
   * @param {string} agreementId 
   * 
   * @returns {Object} agreementData with:
   * - `type`: The fully populated agreementType
   * - `template`: The fully populated agreementTemplate
   * - `statusTitleCase`: The status of the agreement, title cased.
   * - `signerResponses`: The response object, built by `buildSignerResponsesObject`
   * - `cmResponse`: The value from signerResponses for current member.
   * - `cmResponseType`: Equal to...
   *   - `non-signer`: Current member is not a signer
   *   - `no-response`: Current member IS a signer but hasn't responded
   *   - `response`: Current member IS a signer and has responded. Actual status in `cmResponse`
   * - `cmResponseStatusTitleCase`: The status of the current member's response, title cased.
   * - `declineReason`: The decline reason, if relevant and provided.
   * - `pdf.path`: The private URL to the PDF, if it exists.
   * - `nextSteps`: The next steps for the agreement, if any. This is an object with:
   *   - `message`: The message to display above the next step button
   *   - `ctaPath`: The path to the next steps page, href for the next step button
   *   - `ctaText`: The text for the call to action button
   */
  async getAgreement(agreementId) {
    const { getAgreement } = q1AgreementFunctions;
    
    const { data: responseData } = await getAgreement({ agreementId });
    let { agreementData, agreementType, agreementTemplate } = responseData;

    agreementData = 
      await this._enrichAgreementFull(agreementData, agreementType, agreementTemplate);

    return agreementData;
  }

  /** Adds all fields that can be done without an async call. */
  _enrichAgreement(agreementData, agreementType = null, agreementTemplate = null) {
    const statusTitleCase = capFirstChar(agreementData.status);
    const signerResponses = this.buildSignerResponsesObject(agreementData);
    const cmResponse = signerResponses[this.currentMember.id]?.response;
    const cmResponseType = agreementData.signers?.includes(this.currentMember.id)
      ? (cmResponse?.status ? 'response' : 'no-response')
      : 'non-signer';

    const cmResponseStatusTitleCase = capFirstChar(cmResponse?.status);

    // FIXME: Needs refactoring once there are multiple signers
    const declineReason = agreementData?.signerResponseLogs?.[0]?.response?.metadata?.declineReason;

    const enrichedAgreementData = {
      ...agreementData,
      statusTitleCase,
      signerResponses,
      cmResponse, cmResponseType, cmResponseStatusTitleCase,
      declineReason,
    };
    if (agreementType) enrichedAgreementData.type = agreementType;
    if (agreementTemplate) enrichedAgreementData.template = agreementTemplate;

    // Add the next steps if applicable
    this._enrichAgreementNextSteps(enrichedAgreementData);

    return enrichedAgreementData;
  }

  /** Adds `nextSteps` to the agreement data if applicable. */
  _enrichAgreementNextSteps(agreementData) {
    // First figure out if there are any potential next steps
    const { type, status } = agreementData;
    const nextSteps = nextStepsByType[type?.key || type];
    if (!nextSteps || (status !== 'approved')) return null;

    const { activeFor, activeWhen, message, ctaPath, ctaText } = nextSteps;
    const { flag: activeWhenFlag } = activeWhen;     
    const cmFlags = this.currentMember.flags || [];
    let isActive = false;

    // Are next steps active for the current member?
    if (activeFor === 'subject') isActive = (this.currentMember.id === agreementData.subjectId);
    if (activeFor === 'signer') isActive = agreementData.signers?.includes(this.currentMember.id);

    // If so, are they active right now?
    if (activeWhenFlag) isActive = isActive && cmFlags.includes(activeWhenFlag);
    else isActive = false; // IMPROVEME: Add support for more condition types

    // If active then set the nextSteps key
    if (isActive) agreementData.nextSteps = { message, ctaPath, ctaText };
    return null;
  }

  /** Sames as _enrichAgreement, but also adds PDF link. Requires async. */
  async _enrichAgreementFull(agreementData, agreementType = null, agreementTemplate = null) {
    const enrichedAgreementData =
      this._enrichAgreement(agreementData, agreementType, agreementTemplate);

    const pdfPath = enrichedAgreementData.pdf?.path;
    if (pdfPath) enrichedAgreementData.pdf.path = await getPrivateFirestoreUrl(pdfPath);

    return enrichedAgreementData;
  }

  /** Returns array of enriched agreements for the current member. */
  async getMyAgreements() {
    const { getMyAgreements } = q1AgreementFunctions;

    let { data: responseData } = await getMyAgreements();
    
    const agreements = responseData.agreements || [];
    const enrichedAgreements = agreements.map((a) => this._enrichAgreement(a));

    return enrichedAgreements;
  }

  /**
   * Builds an object that makes it easy to get the signer responses by memberId. Only includes 
   * responses that match with signers.
   * 
   * @param {Object} agreementData - Uses fields: `signers`, `signerResponseLogs`.
   * 
   * @returns {Object} with a key for each signer memberId that has responded, value is the 
   *   full log item object from `signerResponseLogs`. Returns empty object (`{}`) if no responses.
   */
  buildSignerResponsesObject(agreementData = {}) {
    const { signerResponseLogs, signers } = agreementData;
    const signerIds = agreementData.signers || [];

    const signerResponses = Object.fromEntries(
      (signerResponseLogs || [])
        .filter(l => (l.verification?.status === 'verified') && signerIds.includes(l.member?.id))
        .map((l) => [l?.member?.id, l])
    );

    return signerResponses;
  }
  
  /**
   * Calls trackResponse and returns the result data.
   * BEFORE CALLING ENSURE:
   * - `agreementManager.ready === true`
   * - `legallyBound === true` if `status === 'approved'`
   */
  async submitResponse({
    agreementId, sentBody, memberNumber, status,
    nameLegal, signatureFont, data,
    legallyBound, // required if status is 'approved
    declineReason, // include this if status is 'declined'
  }) {
    const { trackResponse } = q1AgreementFunctions;

    // These should not happen in prod, parent element should monitor the state & disallow response
    if (!this.ready) throw new Error('The agreement manager is not ready to sign agreements');
    if ((status === 'approved') && !legallyBound) throw new Error('Legally bound must be true');

    const metadata = this.getBrowserMetadata();
    
    // Light data cleanup if at non-approved status
    if (status !== 'approved') {
      // All of the fields in the form are optional during a decline, so we need to fix any empties
      if (!nameLegal?.trim()) nameLegal = '[ Agreement Declined ]';

      // There may be a decline reason provided, if so we put it in the metadata
      if (declineReason) metadata.declineReason = declineReason;
    }

    // Generate the hash of the body
    const bodyHash = await generateHash(sentBody);
    
    const response = {
      agreementId,
      memberNumber,
      nameLegal,
      bodyHash,
      respondedAt: new Date().toISOString(), // serialize now to ensure hash consistency
      status,
      data,
      metadata,
    };
    const signedResponseHash = await generateSignedHash(response, this.keyPair.privateKey);
    const publicKey = await exportPublicKeyToSPKI(this.keyPair.publicKey);

    const requestData = {
      response,
      signedResponseHash,
      member: { publicKey, signatureFont },
    };

    const result = await trackResponse(requestData);
  
    return result.data;
  }

  /** Generates a new key pair and returns it. */
  async generateNewKeyPair() {
    const keyPair = await generateAndStoreKeyPair();
    this.keyPair = keyPair;
    return this.keyPair;
  }

  /** Gets the current key pair. */
  async getKeyPair() {
    const keyPair = await getKeyPair();
    this.keyPair = keyPair;
    return this.keyPair;
  }

  async exportPublicKey() {
    if (!this.keyPair) return '';
    
    return exportPublicKeyToSPKI(this.keyPair.publicKey);
  }

  /** Returns metadata object to include in response. */
  getBrowserMetadata() {
    return {
      userAgent: navigator?.userAgent,
      timeZone: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
      language: navigator?.language || navigator?.userLanguage,
      platform: navigator?.userAgentData?.platform,
      vendor: navigator?.vendor,
    };
  }

  /** Deletes an agreement. Returns true if successful. */
  async deleteMyAgreement(agreementId) {
    const { deleteMyAgreement } = q1AgreementFunctions;

    await deleteMyAgreement({ agreementId });

    return true;
  }

  /** For testing. */
  async _verifySignedHash(signedHash, rawPublicKey, originalData) {
    const publicKey = rawPublicKey instanceof CryptoKey
      ? rawPublicKey
      : await importPublicSpkiKey(rawPublicKey);
      
    const result = await verifySignedHash(signedHash, publicKey, originalData);

    console.log('_verifySignedHash: result = ', result);

    return result;
  }

  /** For testing. */
  async _generateHash(stringValue) {
    return generateHash(stringValue);
  }
}
